import { E, pipe, TE } from "@ihr-radioedit/fp";
import { HTTPError, isAxiosError } from "@ihr-radioedit/sdk-network";
import { GraphQLError, GraphQLResponse } from "./response";
import { shaStr } from "./utils";
/**
 * GraphQLRequest is instantiated with these default options if no other options
 * are passed in. Since this is an object reference it can be tweaked to update
 * how things are invoked during client configuration.
 */
export const defaultRequestOpts = {
    useAPQ: true,
    tolerateErrors: true,
};
export const APQ_NOT_FOUND_CODE = "PersistedQueryNotFound";
// NOTE: we believe TError is being defined as object or std out of caution. For 4.x we could try removing that.
/**
 * This is a simple callable designed to accept a query and return
 */
export const connectionRequestFactory = (conn, options) => (opName, type, query) => {
    return new GraphQLRequest(conn, type, query, opName, options);
};
// Variables and Extensions parameters get JSON serialized and then escaped. This function can do that.
const encodeObject = (obj) => encodeURIComponent(JSON.stringify(obj ?? {}));
export class GraphQLRequest {
    constructor(conn, type, gql, opName, options) {
        this.conn = conn;
        this.type = type;
        this.gql = gql;
        this.opName = opName;
        this.options = { ...defaultRequestOpts, ...(options ?? {}) };
    }
    get client() {
        return this.conn.http;
    }
    query(variables, options) {
        const combinedOpts = { ...this.options, ...(options ?? {}) };
        const extensions = { persistedQuery: { version: 1, sha256Hash: shaStr(this.gql) } };
        const doGet = () => {
            const params = {
                operationName: this.opName,
                variables: encodeObject(variables ?? {}),
                extensions: encodeObject(extensions),
            };
            return pipe(this.request("GET", { params }), 
            // If the get responded with an APQ error, let's try a post. Otherwise just keep response.
            // Apollo is a shit head, and only reads post body OR query string, never both.
            // https://github.com/apollographql/apollo-server/issues/3547
            TE.chain(response => response.hasErrorCondition(APQ_NOT_FOUND_CODE) ? doPost(true) : TE.right(response)));
        };
        const doPost = (withAPQ = false) => {
            const data = {
                operationName: this.opName,
                variables: variables ?? {},
                query: this.gql,
                extensions: withAPQ ? extensions : undefined,
            };
            return this.request("POST", { data });
        };
        const convertError = (task) => pipe(task, 
        // If there are errors and tolerateErrors isn't enabled, make this a left.
        TE.filterOrElse(response => !response.hasError || combinedOpts.tolerateErrors, response => {
            return new GraphQLError("GraphQL Error", response);
        }));
        // Add APQ extension config if enabled.
        if (this.type === "Query" && combinedOpts.useAPQ) {
            // Optimistiacally try this with a GET
            return pipe(doGet(), convertError);
        }
        else {
            return pipe(doPost(false), convertError);
        }
    }
    /**
     * Workaround for non FP-TS code bases. This is pretty common, though using
     * fp-ts is still recommended.
     */
    async queryAsPromise(variables, options) {
        const response = await this.query(variables, options)();
        if (E.isLeft(response)) {
            throw response.left;
        }
        return response.right;
    }
    request(method, config) {
        return pipe(TE.tryCatch(async () => this.client.request({ method, ...config }), (reason) => reason), TE.filterOrElse(response => String(response.headers["content-type"]).includes("application/json"), response => new HTTPError(`Expected response type to contain 'application/json', got ${response.headers["content-type"]}`, response)), TE.map(response => GraphQLResponse.fromAxios(response)), TE.mapLeft(err => {
            console.log(err, isAxiosError(err));
            return err;
        }));
    }
}
//# sourceMappingURL=request.js.map